import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { ease } from "@/lib/motion";
import { 
  Layers, 
  Target, 
  LineChart, 
  Zap,
  ArrowRight,
  Check,
  ChevronDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { resolveIcon } from "@/lib/iconResolver";

interface ValuePillar {
  id: string;
  title: string;
  tagline: string;
  description: string;
  mobileDescription: string;
  icon: React.ElementType;
  features: string[];
  stat: { value: string; label: string };
  cta: { text: string; link: string };
  accentColor: string;
}

const defaultPillars: ValuePillar[] = [
  {
    id: "systems",
    title: "Systems, Not Services",
    tagline: "Compounding infrastructure",
    description: "We build interconnected growth systems that compound over time. Not isolated tactics that require constant reinvention.",
    mobileDescription: "Interconnected growth systems that compound over time. Not isolated tactics.",
    icon: Layers,
    features: ["Integrated channel orchestration", "Self-reinforcing feedback loops", "Scalable playbook architecture"],
    stat: { value: "40%", label: "less execution overhead" },
    cta: { text: "See our approach", link: "/about" },
    accentColor: "hsl(216, 99%, 33%)",
  },
  {
    id: "precision",
    title: "Data-Driven Execution",
    tagline: "Right audience, right moment",
    description: "Advanced audience intelligence that identifies high-intent prospects before your competitors. Turning data into decisive action.",
    mobileDescription: "Audience intelligence that identifies high-intent prospects before competitors.",
    icon: Target,
    features: ["Intent-based segmentation", "Predictive audience modeling", "Cross-channel attribution"],
    stat: { value: "2.4x", label: "higher conversion rates" },
    cta: { text: "Explore services", link: "/services" },
    accentColor: "hsl(169, 66%, 42%)",
  },
  {
    id: "intelligence",
    title: "Compounding Advantage",
    tagline: "Clarity over complexity",
    description: "Real-time analytics and insights that inform every decision. Transforming raw data into strategic advantage.",
    mobileDescription: "Real-time analytics that transform raw data into strategic advantage.",
    icon: LineChart,
    features: ["Unified performance dashboards", "Automated anomaly detection", "Strategic insight reports"],
    stat: { value: "100%", label: "decision visibility" },
    cta: { text: "View analytics", link: "/analytics" },
    accentColor: "hsl(220, 25%, 45%)",
  },
  {
    id: "velocity",
    title: "Operational Clarity",
    tagline: "Speed without sacrifice",
    description: "Rapid deployment frameworks that accelerate time-to-impact without compromising strategic integrity or brand standards.",
    mobileDescription: "Rapid deployment that accelerates time-to-impact without compromise.",
    icon: Zap,
    features: ["7-day sprint launches", "Pre-built optimization templates", "Continuous iteration cycles"],
    stat: { value: "3x", label: "faster to market" },
    cta: { text: "Start now", link: "/contact" },
    accentColor: "hsl(275, 50%, 52%)",
  },
];

// CMS content interface - text fields only, layout stays locked
export interface ValuePropositionContent {
  eyebrow: string;
  headline: string;
  subtitle: string;
  pillars: {
    id: string;
    title: string;
    tagline: string;
    description: string;
    mobileDescription: string;
    icon: string;
    features: string[];
    stat: { value: string; label: string };
    cta: { text: string; link: string };
    accentColor: string;
  }[];
}

export const valuePropositionDefaults: ValuePropositionContent = {
  eyebrow: "Why Xperfomax",
  headline: "Four pillars of structured growth",
  subtitle: "What differentiates structured growth from random tactics.",
  pillars: defaultPillars.map(p => ({
    ...p,
    icon: p.id === "systems" ? "Layers" : p.id === "precision" ? "Target" : p.id === "intelligence" ? "LineChart" : "Zap",
  })),
};

// Build runtime pillars from CMS content
const buildPillars = (content: ValuePropositionContent): ValuePillar[] => {
  return content.pillars.map((p) => ({
    ...p,
    icon: resolveIcon(p.icon),
  }));
};

// Mobile Accordion Item with progressive disclosure
const MobileAccordionItem = ({ 
  pillar, 
  isExpanded, 
  onToggle 
}: { 
  pillar: ValuePillar; 
  isExpanded: boolean; 
  onToggle: () => void;
}) => {
  const [showAllFeatures, setShowAllFeatures] = useState(false);
  const Icon = pillar.icon;

  return (
    <div className="border-b border-border last:border-b-0">
      {/* Accordion Header */}
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between py-4 px-1 min-h-[56px] text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
        aria-expanded={isExpanded}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: `${pillar.accentColor}15` }}
          >
            <Icon
              className="w-4 h-4"
              style={{ color: pillar.accentColor }}
            />
          </div>
          <span className={`font-medium text-sm ${isExpanded ? 'text-foreground' : 'text-muted-foreground'}`}>
            {pillar.title}
          </span>
        </div>
        <ChevronDown 
          className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
        />
      </button>

      {/* Accordion Content */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="pb-5 px-1">
              {/* 1. Metric Card - PROOF FIRST */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.15, delay: 0.05 }}
                className="w-full p-4 rounded-lg border border-border bg-card/50 mb-4"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="text-2xl font-display font-bold"
                    style={{ color: pillar.accentColor }}
                  >
                    {pillar.stat.value}
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {pillar.stat.label}
                  </span>
                </div>
              </motion.div>

              {/* 2. Compressed Description */}
              <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                {pillar.mobileDescription}
              </p>

              {/* 3. Progressive Disclosure Bullets */}
              <ul className="space-y-2 mb-4">
                {/* Always show first bullet */}
                <li className="flex items-start gap-2">
                  <div
                    className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: `${pillar.accentColor}15` }}
                  >
                    <Check
                      className="w-2.5 h-2.5"
                      style={{ color: pillar.accentColor }}
                    />
                  </div>
                  <span className="text-sm text-foreground">{pillar.features[0]}</span>
                </li>

                {/* Remaining bullets with progressive disclosure */}
                <AnimatePresence>
                  {showAllFeatures && pillar.features.slice(1).map((feature, index) => (
                    <motion.li
                      key={feature}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.15, delay: index * 0.05 }}
                      className="flex items-start gap-2"
                    >
                      <div
                        className="w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                        style={{ backgroundColor: `${pillar.accentColor}15` }}
                      >
                        <Check
                          className="w-2.5 h-2.5"
                          style={{ color: pillar.accentColor }}
                        />
                      </div>
                      <span className="text-sm text-foreground">{feature}</span>
                    </motion.li>
                  ))}
                </AnimatePresence>
              </ul>

              {/* Show more/less toggle */}
              {pillar.features.length > 1 && (
                <button
                  onClick={() => setShowAllFeatures(!showAllFeatures)}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors mb-4 inline-flex items-center gap-1"
                >
                  {showAllFeatures ? (
                    <>Show less <ChevronDown className="w-3 h-3 rotate-180" /></>
                  ) : (
                    <>Show {pillar.features.length - 1} more <ChevronDown className="w-3 h-3" /></>
                  )}
                </button>
              )}

              {/* 4. CTA - Immediately after bullets */}
              <Link 
                to={pillar.cta.link}
                className="inline-flex items-center gap-1.5 text-sm text-foreground hover:text-primary transition-colors group"
              >
                {pillar.cta.text}
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Desktop Tab Navigation (unchanged from original)
const DesktopTabNav = ({ 
  pillars, 
  activeTab, 
  setActiveTab 
}: { 
  pillars: ValuePillar[]; 
  activeTab: string; 
  setActiveTab: (id: string) => void;
}) => (
  <motion.div
    className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0 -mx-6 px-6 lg:mx-0 lg:px-0"
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.5, delay: 0.2, ease }}
  >
    {pillars.map((pillar, index) => {
      const isActive = activeTab === pillar.id;
      const Icon = pillar.icon;

      return (
        <button
          key={pillar.id}
          onClick={() => setActiveTab(pillar.id)}
          className={`
            relative flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all duration-300
            min-w-[200px] lg:min-w-0 flex-shrink-0 lg:flex-shrink
            ${isActive 
              ? "bg-card border border-border shadow-sm" 
              : "hover:bg-muted/50"
            }
          `}
        >
          {/* Active indicator bar */}
          <motion.div
            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 rounded-full"
            style={{ backgroundColor: pillar.accentColor }}
            initial={false}
            animate={{
              height: isActive ? "60%" : "0%",
              opacity: isActive ? 1 : 0,
            }}
            transition={{ duration: 0.25, ease }}
          />

          {/* Icon */}
          <div
            className={`
              w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-300
              ${isActive ? "" : "bg-muted/50"}
            `}
            style={{
              backgroundColor: isActive ? `${pillar.accentColor}15` : undefined,
            }}
          >
            <Icon
              className="w-4 h-4 transition-colors duration-300"
              style={{ color: isActive ? pillar.accentColor : "hsl(var(--muted-foreground))" }}
            />
          </div>

          {/* Text */}
          <div className="flex-1 min-w-0">
            <p
              className={`font-medium text-sm transition-colors duration-300 truncate ${
                isActive ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {pillar.title}
            </p>
            <p className="text-xs text-muted-foreground truncate hidden lg:block">
              {pillar.tagline}
            </p>
          </div>

          {/* Step number */}
          <span
            className={`text-xs font-medium transition-colors duration-300 ${
              isActive ? "text-foreground" : "text-muted-foreground/50"
            }`}
          >
            0{index + 1}
          </span>
        </button>
      );
    })}
  </motion.div>
);

// Desktop Content Panel (unchanged from original)
const DesktopContentPanel = ({ activePillar }: { activePillar: ValuePillar }) => (
  <div className="relative min-h-[400px] lg:min-h-[360px]">
    <AnimatePresence mode="wait">
      <motion.div
        key={activePillar.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.35, ease }}
        className="grid md:grid-cols-2 gap-8 lg:gap-12"
      >
        {/* Left: Description + Features */}
        <div>
          <h3 className="text-xl md:text-2xl font-display font-semibold text-foreground mb-3">
            {activePillar.title}
          </h3>

          <p className="text-muted-foreground leading-relaxed mb-6">
            {activePillar.description}
          </p>

          {/* Features */}
          <ul className="space-y-3 mb-8">
            {activePillar.features.map((feature, index) => (
              <motion.li
                key={feature}
                className="flex items-start gap-3"
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.1 + index * 0.05, duration: 0.3, ease }}
              >
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: `${activePillar.accentColor}15` }}
                >
                  <Check
                    className="w-3 h-3"
                    style={{ color: activePillar.accentColor }}
                  />
                </div>
                <span className="text-sm text-foreground">{feature}</span>
              </motion.li>
            ))}
          </ul>

          {/* CTA */}
          <Button variant="outline" size="sm" asChild className="group">
            <Link to={activePillar.cta.link}>
              {activePillar.cta.text}
              <ArrowRight className="ml-2 h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>

        {/* Right: Stat card */}
        <div className="flex items-start justify-center md:justify-end">
          <motion.div
            className="relative p-8 md:p-10 rounded-2xl border border-border bg-card/50 backdrop-blur-sm max-w-[280px] w-full"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.15, duration: 0.4, ease }}
          >
            {/* Decorative corner */}
            <div
              className="absolute top-0 right-0 w-16 h-16 rounded-bl-2xl opacity-10"
              style={{ backgroundColor: activePillar.accentColor }}
            />

            {/* Icon */}
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center mb-6"
              style={{ backgroundColor: `${activePillar.accentColor}15` }}
            >
              <activePillar.icon
                className="w-6 h-6"
                style={{ color: activePillar.accentColor }}
              />
            </div>

            {/* Stat */}
            <div className="mb-2">
              <span
                className="text-4xl md:text-5xl font-display font-bold"
                style={{ color: activePillar.accentColor }}
              >
                {activePillar.stat.value}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {activePillar.stat.label}
            </p>

            {/* Animated progress bar */}
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl overflow-hidden"
              style={{ backgroundColor: `${activePillar.accentColor}10` }}
            >
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: activePillar.accentColor }}
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 4, ease: "linear" }}
                key={activePillar.id}
              />
            </motion.div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>
  </div>
);

interface ValuePropositionProps {
  content?: ValuePropositionContent;
}

const ValueProposition = ({ content: c = valuePropositionDefaults }: ValuePropositionProps) => {
  const pillars = buildPillars(c);
  const [activeTab, setActiveTab] = useState<string>(pillars[0].id);
  const [mobileExpandedId, setMobileExpandedId] = useState<string>(pillars[0].id);
  const activePillar = pillars.find((p) => p.id === activeTab)!;
  const isMobile = useIsMobile();

  const handleMobileToggle = (id: string) => {
    setMobileExpandedId(mobileExpandedId === id ? "" : id);
  };

  return (
    <section className="py-10 md:py-16 lg:py-20 bg-background relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--foreground)) 1px, transparent 0)`,
          backgroundSize: "32px 32px",
        }}
      />

      <div className="container-wide relative z-10">
        <div className="max-w-2xl mb-8 md:mb-12 lg:mb-16">
          <motion.div
            className="flex items-center gap-3 mb-4"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease }}
          >
            <div className="w-8 h-px bg-primary" />
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">
              {c.eyebrow}
            </span>
          </motion.div>

          <motion.h2
            className="text-2xl md:text-3xl lg:text-4xl font-display font-semibold text-foreground mb-3 md:mb-4"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1, ease }}
          >
            {c.headline}
          </motion.h2>

          <motion.p
            className="text-muted-foreground text-base md:text-lg hidden md:block"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.15, ease }}
          >
            {c.subtitle}
          </motion.p>
        </div>

        {isMobile ? (
          <motion.div
            className="border-t border-border"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.3, delay: 0.2 }}
          >
            {pillars.map((pillar) => (
              <MobileAccordionItem
                key={pillar.id}
                pillar={pillar}
                isExpanded={mobileExpandedId === pillar.id}
                onToggle={() => handleMobileToggle(pillar.id)}
              />
            ))}
          </motion.div>
        ) : (
          <div className="grid lg:grid-cols-[300px_1fr] gap-8 lg:gap-12">
            <DesktopTabNav 
              pillars={pillars} 
              activeTab={activeTab} 
              setActiveTab={setActiveTab} 
            />
            <DesktopContentPanel activePillar={activePillar} />
          </div>
        )}
      </div>
    </section>
  );
};

export default ValueProposition;
